import React, { useState, useEffect, useRef } from 'react'
import Editor from 'components/editor'
import { IContextMenuState, ITableViewColumn, ICellValue, EditorContent, IUpdateTableViewCell } from 'types'
import {
  INITIAL_SELECTED_CELL,
  DEFAULT_FORM_COLUMN_WIDTH,
  FRAMELESS_FORM_KINDS
} from 'components/spreadsheet/constants/const'
import { Dots, Drag, Lock, Join } from 'components/icons'
import ColumnMenu from 'components/spreadsheet/components/menu/views/column'
import { FormFieldLabel } from 'components/spreadsheet/components/form/fieldLabel'
import Cell from 'components/spreadsheet/components/cell'
import { updateColumn, updateView, refreshSelectOptions } from 'components/spreadsheet/contexts/data/actions'
import Button from 'components/button'
import { BLUE_BUTTON } from 'components/save-buttons'
import { useApplicationStore } from 'hooks/application'
import { isFieldValueEmpty, isEditorEmpty, isColumnLockedByRules } from 'components/spreadsheet/helpers/functions'
import { isValidValue } from 'components/spreadsheet/helpers/validation'
import { useProject } from 'hooks/useProject'
import DeleteColumnModal from 'components/spreadsheet/components/modal/views/deletecolumn'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { cancelTimeout, requestTimeout, TimeoutID } from 'helpers/timer'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import api from 'helpers/api'
import EditColumnMenu from '../menu/views/editcolumn'
import FormCellMenu from '../menu/views/formcell'
import { hashString } from 'helpers/utils'
import Input from 'components/input'
import { useInlineRename } from 'components/spreadsheet/hooks/useInlineRename'
import { COMPACT_RICH_TEXT, DESCRIPTION_FIELD } from 'components/spreadsheet/components/column/description-field'

const createFormValueState = (values: Record<string, ICellValue>, columns: ITableViewColumn[]) => {
  const newValues: Record<string, ICellValue> = {}
  for (const column of columns) {
    newValues[column.publicId] = values[column.publicId] ? values[column.publicId] : null
  }

  return newValues
}

// A field's frame: Input's look (36px, 8px corners, the field fill, a quiet border that darkens on hover and
// turns blue with a soft ring while it's in use) around the cell that edits the value. The cell keeps its
// editors, so every column kind works as it does in the table. form-field-cell tells the spreadsheet a
// click here is on a field rather than the page.
// The landing line for a dragged field: in the middle of the 16px gap beside a half-width field, or the 20px gap
// above or below one across the form.
const dropLine = (fullWidth: boolean, side: 'before' | 'after'): string => {
  if (fullWidth) return `inset-x-0 h-0.5 ${side === 'before' ? '-top-[11px]' : '-bottom-[11px]'}`
  return `inset-y-0 w-0.5 ${side === 'before' ? '-left-[9px]' : '-right-[9px]'}`
}

const FORM_FIELD = [
  // Clipped to its corners, so nothing square in the cell paints over the inside of the rounded border (the menus
  // it opens are portalled, out of the way of the clip).
  'form-field-cell relative min-w-0 overflow-hidden rounded-lg border border-divider bg-field',
  // The value sits on the field's own fill.
  '[&_.cell-value]:!bg-transparent',
  'transition-[border-color,box-shadow] duration-150 hover:not-focus-within:border-faint',
  'focus-within:border-blue focus-within:ring-3 focus-within:ring-blue/15',
  // The editor fills the frame: no outline or fill of its own, as tall as its text and growing a line at a
  // time as it's typed in, without a corner to drag.
  '[&_.cell-value-editing]:!border-0 [&_.cell-value-editing]:!bg-transparent',
  '[&_textarea.cell-value-editing]:!h-auto [&_textarea.cell-value-editing]:![field-sizing:content]',
  '[&_textarea.cell-value-editing]:!min-h-[34px] [&_textarea.cell-value-editing]:!py-[7px]',
  '[&_textarea.cell-value-editing]:!leading-5 [&_textarea.cell-value-editing]:!resize-none',
  // A one-line input (a date, a number) is the field's height.
  '[&_input.cell-value-editing]:!h-[34px]',
  // 12px in at both ends, as a text field's text is.
  '[--cell-pl:12px] [&_.cell-value]:!pr-3 [&_.cell-value-editing]:!pr-3',
  // A broken validation rule's mark sits in that gutter at the field's end, centred on its first line, and the
  // field keeps clear of it: a dropdown's chevron, a date's calendar button and typed text stop short of the mark.
  '[&_[data-testid=cell-issue]]:!right-3 [&_[data-testid=cell-issue]]:!top-[9px]',
  'has-[[data-testid=cell-issue]]:[&_.cell-value]:!pr-9 has-[[data-testid=cell-issue]]:[&_.cell-value-editing]:!pr-9',
  'has-[[data-testid=cell-issue]]:[&_[data-date-field]]:!pr-8'
].join(' ')

// The cell without its grid lines or the browser's focus outline (the frame shows focus), rounded to sit
// inside the frame, at least as tall as a text field and taller when its text is.
const FORM_FIELD_CELL =
  'flex items-center border-t-0 after:hidden outline-none rounded-[7px] h-auto max-h-none min-h-[34px]'

// A description to read: muted, at the label's 13px on 20px lines, with nothing around it, nor the empty line
// its writer may have left at its end.
const READ_ONLY_DESCRIPTION = `text-muted ${COMPACT_RICH_TEXT} [&_.RichEditor-editor]:!p-0 [&_.public-DraftEditor-content>div>[data-block]:last-child:not(:first-child):has(br)]:hidden`

const TITLE_TEXT = 'text-[20px] font-semibold leading-7 tracking-[-0.01em]'

// The form's name, which is its view's. Its owner renames it in place, as a view's tab is renamed: it shows the hover
// fill to say it can be, and a click turns it into a text field holding the name; Enter or a click away saves it,
// Escape keeps the old one.
const FormTitle: React.FC<{ name: string; canRename: boolean; onRename: (name: string) => void }> = ({
  name,
  canRename,
  onRename
}) => {
  const rename = useInlineRename((_id, newName) => onRename(newName))

  if (!canRename) return <h1 className={`truncate ${TITLE_TEXT}`}>{name}</h1>

  if (rename.renamingId !== null) {
    return (
      <h1 className={TITLE_TEXT}>
        {/* Set out by the field's inset, so the name stays where it was read. */}
        <Input
          {...rename.inputProps}
          aria-label="View name"
          className={`-mx-[13px] h-auto w-[calc(100%+26px)] py-0 ${TITLE_TEXT}`}
        />
      </h1>
    )
  }

  return (
    <h1 className={TITLE_TEXT}>
      <span
        role="button"
        tabIndex={0}
        aria-label={`Rename ${name}`}
        className="-mx-[13px] block cursor-text truncate rounded-md border border-transparent px-3 transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-blue"
        onClick={() => rename.start('view', name)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            rename.start('view', name)
          }
        }}
      >
        {name}
      </span>
    </h1>
  )
}

// The form's description, under its name, as the people filling it in read it. Its owner edits it in place: it
// shows the hover fill to say it can be, and a click turns it into the description field, until a press away from
// it or Escape. With none, its owner sees that there's none.
const FormDescription: React.FC<{
  description: EditorContent | null | undefined
  editable: boolean
  editorId: string
  tablePublicId: string
  onChange: (description: EditorContent) => void
}> = ({ description, editable, editorId, tablePublicId, onChange }) => {
  // What's been typed, shown at once when editing ends rather than once the save has come back.
  const [draft, setDraft] = useState<EditorContent | null | undefined>(description)
  const [editing, setEditing] = useState<boolean>(false)
  const fieldRef = useRef<HTMLDivElement>(null)
  const empty = !draft || isEditorEmpty(draft)
  const resources = [{ resource: 'document_table' as const, publicId: tablePublicId }]

  useEffect(() => {
    if (!editing) return
    // Into the text, at its end, as the click asked.
    const focusEditor = () => fieldRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus()
    const retry = window.setTimeout(focusEditor, 50)
    focusEditor()
    // A press anywhere but the field or a menu opened from it (Insert, a link's) ends editing.
    const pressAway = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target || fieldRef.current?.contains(target) || target.closest('[data-menu]')) return
      setEditing(false)
    }
    document.addEventListener('mousedown', pressAway)
    return () => {
      window.clearTimeout(retry)
      document.removeEventListener('mousedown', pressAway)
    }
  }, [editing])

  if (!editable) {
    return empty ? null : (
      <div className={`mt-2 ${READ_ONLY_DESCRIPTION}`}>
        <Editor databaseDoc={draft} readOnly={true} editorId={editorId} resources={resources} />
      </div>
    )
  }

  if (editing) {
    return (
      // Set out by the field's inset, so the text stays where it was read.
      <div
        ref={fieldRef}
        className={`-mx-[13px] mt-1.5 ${DESCRIPTION_FIELD} [&_.public-DraftEditor-content]:!min-h-10`}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setEditing(false)
        }}
      >
        <Editor
          databaseDoc={draft}
          readOnly={false}
          editorId={editorId}
          border
          className="!rounded-md !border-0 !px-3 !pt-[7px] !pb-1"
          compactToolbar
          onChange={(value) => {
            setDraft(value)
            onChange(value)
          }}
          resources={resources}
        />
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Edit form description"
      // As much room round the text as the field it turns into, so neither the text nor the box moves on a click.
      className={`-mx-[13px] mt-1.5 cursor-text rounded-md border border-transparent px-3 py-[7px] transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-blue ${READ_ONLY_DESCRIPTION}`}
      onClick={() => setEditing(true)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          setEditing(true)
        }
      }}
    >
      {empty ? (
        <span className="text-faint">No form description</span>
      ) : (
        <Editor
          key={hashString(JSON.stringify(draft))}
          databaseDoc={draft}
          readOnly={true}
          editorId={editorId}
          resources={resources}
        />
      )}
    </div>
  )
}

const FormComponent: React.FC = () => {
  const { project } = useProject()
  const {
    spreadsheetData,
    setSpreadsheetData,
    uniqueNumber,
    selectedCell,
    setSelectedCell,
    handleCreateRow,
    handleKeyDownForm,
    setIsFocused
  } = useDataContext()

  const [editColumnMenu, setEditColumnMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [deleteColumnModal, setDeleteColumnModal] = useState<boolean>(false)
  const [changeColumnMenu, setChangeColumnMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const [cellMenu, setCellMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)
  const { displayErrorMessage, setSnackbarMessage } = useApplicationStore()
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false)
  const [currentViewId, setCurrentViewId] = useState<string>(spreadsheetData.viewDetails.publicId)
  const controllerRef = useRef<AbortController | null>(null)
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)

  const descriptionTimeout = useRef<TimeoutID | null>(null)

  // The columns we use to build the form
  const columns = spreadsheetData.viewDetails.columns.filter(
    (column) =>
      !spreadsheetData.userConfiguration.hiddenColumns.find((hiddenColumnId) => hiddenColumnId === column.publicId)
  )

  const [formValues, setFormValues] = useState(() => createFormValueState({}, columns))

  // If column info is changed, update the form's values
  // Use functional update to avoid stale closure - formValues must come from callback parameter
  useEffect(() => {
    setFormValues((currentFormValues) => createFormValueState(currentFormValues, columns))
  }, [columns])

  // If new view is selected
  useEffect(() => {
    if (currentViewId !== spreadsheetData.viewDetails.publicId) {
      setFormSubmitted(false)
      setFormValues(createFormValueState({}, columns))
      setCurrentViewId(spreadsheetData.viewDetails.publicId)
    }
  }, [spreadsheetData.viewDetails])

  // Dragging a field to reorder the form, as the column order menu does: the field being dragged, and where it
  // would land (before or after a field), shown as the menus' blue line in the gap beside or between them.
  const [dragging, setDragging] = useState<string | null>(null)
  const [landing, setLanding] = useState<{ columnId: string; side: 'before' | 'after'; beside: boolean } | null>(null)

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>, columnId: string) => {
    if (spreadsheetData.isAdmin) {
      event.dataTransfer.setData('columnId', columnId)
      event.dataTransfer.effectAllowed = 'move'
      setDragging(columnId)
    }
  }

  const endDrag = () => {
    setDragging(null)
    setLanding(null)
  }

  // Over a field: before or after it, by which half the pointer is in (left or right for a half-width field, which
  // sits beside another, top or bottom for one across the form).
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>, columnId: string, fullWidth: boolean) => {
    if (!spreadsheetData.isAdmin || !dragging) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    if (columnId === dragging) {
      if (landing) setLanding(null)
      return
    }
    const box = event.currentTarget.getBoundingClientRect()
    const before = fullWidth ? event.clientY < box.top + box.height / 2 : event.clientX < box.left + box.width / 2
    const side = before ? 'before' : 'after'
    if (landing?.columnId !== columnId || landing.side !== side) setLanding({ columnId, side, beside: !fullWidth })
  }

  const handleOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const draggedColumnId = event.dataTransfer.getData('columnId') || dragging
    if (spreadsheetData.isAdmin && draggedColumnId && landing && landing.columnId !== draggedColumnId) {
      // Positions in the view's columns, hidden ones included, which is where the order is kept.
      const all = spreadsheetData.viewDetails.columns
      const from = all.findIndex((column) => column.publicId === draggedColumnId)
      const target = all.findIndex((column) => column.publicId === landing.columnId)
      if (from !== -1 && target !== -1) {
        const after = landing.side === 'after'
        // Where the field ends up once it's out of its old place.
        const newIndex = from < target ? (after ? target : target - 1) : after ? target + 1 : target
        if (newIndex !== from) setSpreadsheetData({ type: 'CHANGE_COLUMN_ORDER', columnId: draggedColumnId, newIndex })
        // Dropped beside a half-width field, a field across the form becomes half width too, so the two sit side by
        // side, as Set form width to 1 column would make it.
        const dragged = all[from]
        if (landing.beside && (dragged.formWidth || DEFAULT_FORM_COLUMN_WIDTH) === 2) {
          updateColumn(
            project.publicId,
            'formWidth',
            spreadsheetData,
            setSpreadsheetData,
            draggedColumnId,
            1,
            () => {},
            (error) => displayErrorMessage(error)
          )
        }
      }
    }
    endDrag()
  }

  const setFieldValue = async (columnId: string, value: ICellValue) => {
    // make sure values are saved in the state before calling preview row
    setFormValues((existingFormValues) => {
      return { ...existingFormValues, [columnId]: value }
    })

    // check if any of the of the columns needs to display pre-calculated data e.g. script
    // TODO: add join to validation
    const previewColumns = columns.filter((col) => col.scriptEnabled || col.formulaEnabled || col.isJoined)
    if (previewColumns.length > 0) {
      // abort any call made to preview row if any has been initiated
      if (controllerRef.current) {
        controllerRef.current.abort()
      }
      const controller = new AbortController()
      controllerRef.current = controller
      const signal = controllerRef.current.signal
      try {
        const previewRowRequest = await api.previewRow(
          project.publicId,
          spreadsheetData.activeTableView,
          { ...formValues, [columnId]: value },
          signal
        )
        setFormValues((formValues) => {
          const newFormValues = { ...formValues }
          for (const column of previewColumns) {
            newFormValues[column.publicId] = previewRowRequest.data[column.publicId]
          }
          return newFormValues
        })
      } catch (e) {
        displayErrorMessage(e)
      }
    }
  }

  const handleOnValueChange = async (cells: IUpdateTableViewCell, onSuccess: () => void) => {
    await setFieldValue(cells.cells[0].columnId, cells.cells[0].value)
    onSuccess()
  }

  const isFieldReadOnly = (column: ITableViewColumn) =>
    !spreadsheetData.isContributor ||
    isColumnLockedByRules(spreadsheetData.userConfiguration.rowLockSettings, column.publicId) ||
    column.isJoined ||
    column.isSynced ||
    column.scriptEnabled ||
    column.formulaEnabled

  const handleSubmit = async () => {
    if (submitting) return
    setSubmitting(true)

    // We need to remove keys with  and values that are:
    // - Empty strings : Empty strings are used as default values for keys
    // - Script and joined values: They are calculated values in the BE
    const allowedColumnValues = columns
      .filter((column) => !(column.isJoined || column.scriptEnabled || column.formulaEnabled))
      .map((column) => column.publicId)
    const values = Object.keys(formValues).reduce(
      (acc, key) => {
        if (allowedColumnValues.includes(key) && formValues[key] !== '') {
          acc[key] = formValues[key]
        }
        return acc
      },
      {} as Record<string, ICellValue>
    )

    // Check that required fields have a value
    for (const column of columns) {
      if (
        allowedColumnValues.includes(column.publicId) &&
        column.required &&
        isFieldValueEmpty(column.kind, values[column.publicId])
      ) {
        setConfirmSubmit(false)
        setSubmitting(false)
        setSnackbarMessage({
          message: `'${column.name}' is required. Fill it in before you submit the form.`,
          status: 'error'
        })
        return
      }
    }

    // Check that values are valid
    for (const column of columns) {
      if (spreadsheetData && spreadsheetData.rows) {
        const validValue = isValidValue(
          formValues[column.publicId],
          null,
          spreadsheetData.columnValuesCount,
          column,
          formValues,
          spreadsheetData?.viewDetails.type,
          true
        )

        if (validValue.error) {
          if (validValue.warn) setSnackbarMessage({ status: 'warning', message: validValue.errorMessage })
          else {
            setConfirmSubmit(false)
            setSubmitting(false)
            setSnackbarMessage({ status: 'error', message: validValue.errorMessage })
            return
          }
        }
      }
    }

    try {
      await handleCreateRow(values, undefined, true)
      setFormValues(createFormValueState({}, columns))
      setFormSubmitted(true)
      setSelectedCell(INITIAL_SELECTED_CELL)
      setConfirmSubmit(false)
      setSnackbarMessage({ message: 'Successfully submitted', status: 'success' })
      setSubmitting(false)
    } catch (e) {
      displayErrorMessage(e)
      setConfirmSubmit(false)
      setSubmitting(false)
    }
  }

  const handleResetSubmit = async () => {
    setFormSubmitted(false)
    await refreshSelectOptions(setSpreadsheetData, spreadsheetData)
  }

  const handleViewUpdateSuccess = () => {
    setSnackbarMessage({ status: 'success', message: 'The view description was updated successfully!' })
  }

  const handleViewUpdateFailure = (error: any) => {
    displayErrorMessage(error)
  }

  const handleDescriptionChange = (description: EditorContent) => {
    if (descriptionTimeout.current !== null) cancelTimeout(descriptionTimeout.current)

    descriptionTimeout.current = requestTimeout(() => {
      updateView(
        'description',
        spreadsheetData,
        setSpreadsheetData,
        spreadsheetData.viewDetails.publicId,
        isEditorEmpty(description) ? null : description,
        project.publicId,
        handleViewUpdateSuccess,
        handleViewUpdateFailure
      )
    }, 1000)
  }

  const disable = spreadsheetData.userConfiguration.unsavedChanges || submitting

  const cellMenuColumn = cellMenu.open ? columns.find((column) => column.publicId === cellMenu.columnId) : undefined

  return (
    <div className="form-container relative mx-auto w-full max-w-[720px] px-4 py-8 max-lg:px-2.5 max-lg:py-2.5 text-[13px] text-body">
      {/* A card, as a settings section is: the form's name and description as its header, then a field per
          column, each a label over a text field, as Field and Input draw them elsewhere. */}
      <div className="rounded-lg border border-divider bg-surface">
        <header className="px-6 pt-6 pb-5">
          <FormTitle
            name={spreadsheetData.viewDetails.name}
            canRename={spreadsheetData.isAdmin && !spreadsheetData.viewDetails.isDefault}
            onRename={(name) =>
              updateView(
                'name',
                spreadsheetData,
                setSpreadsheetData,
                spreadsheetData.viewDetails.publicId,
                name,
                project.publicId,
                () => setSnackbarMessage({ status: 'success', message: 'View name updated!' }),
                (error: string) => displayErrorMessage(error)
              )
            }
          />
          {!formSubmitted && (
            <FormDescription
              key={spreadsheetData.viewDetails.publicId}
              description={spreadsheetData.viewDetails.description}
              editable={spreadsheetData.isAdmin}
              editorId={spreadsheetData.viewDetails.publicId}
              tablePublicId={spreadsheetData.tableDetails.publicId}
              onChange={handleDescriptionChange}
            />
          )}
        </header>

        {!formSubmitted && (
          <>
            <div className="grid grid-cols-2 gap-x-4 gap-y-5 px-6 pb-6 max-sm:grid-cols-1">
              {columns.map((column: ITableViewColumn, columnNumber: number) => {
                const selected = selectedCell.rowId === 'new-row-form' && selectedCell.columnId === column.publicId
                const descriptionKey = hashString(JSON.stringify(column.description?.content ?? ''))
                const formWidth = column.formWidth || DEFAULT_FORM_COLUMN_WIDTH // Lets make default full for now (same as current behaviour)
                const isColumnLocked = isColumnLockedByRules(
                  spreadsheetData.userConfiguration.rowLockSettings,
                  column.publicId
                )
                return (
                  <div
                    data-testid="form-column"
                    key={columnNumber}
                    className={`group/field relative flex min-w-0 flex-col gap-1.5 ${formWidth === 2 ? 'col-span-full' : ''} ${dragging === column.publicId ? 'opacity-40' : ''}`}
                    onDragStart={(event) => handleDragStart(event, column.publicId)}
                    onDragOver={(event) => handleDragOver(event, column.publicId, formWidth === 2)}
                    onDrop={handleOnDrop}
                    onDragEnd={endDrag}
                    draggable={spreadsheetData.isAdmin && !isColumnLocked}
                  >
                    {/* The label: the column's name, then what limits it. */}
                    <div className="flex min-h-6 items-center gap-1.5 font-medium">
                      <span id={`form-field-name-${column.publicId}`} className="min-w-0 truncate">
                        {column.name}
                      </span>
                      {/* account for legacy forms where a column could be both locked through a join and required */}
                      {!column.isJoined && column.required && (
                        <>
                          <span className="text-red" aria-hidden>
                            *
                          </span>
                          <span id={`form-field-required-${column.publicId}`} className="sr-only">
                            Required Field
                          </span>
                        </>
                      )}
                      {isColumnLocked && (
                        <span className="flex shrink-0 text-muted">
                          <Lock />
                        </span>
                      )}
                      {column.isJoined && (
                        <span className="flex shrink-0 text-muted">
                          <Join />
                        </span>
                      )}
                      {spreadsheetData.isAdmin && !isColumnLocked && (
                        // A real button, so it's reachable by keyboard and announced by name.
                        <Button
                          internalType="transparent"
                          // Only on the question pointed at or being answered, or whose menu is open, so the form
                          // reads calmly; always on touch screens, which have no hover. It keeps its space when hidden.
                          className={`ml-auto h-6 w-6 shrink-0 p-0 rounded text-muted transition-opacity hover:text-body hover:bg-surface-hover group-hover/field:opacity-100 group-focus-within/field:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 ${editColumnMenu.open && editColumnMenu.columnId === column.publicId ? 'opacity-100' : 'opacity-0'}`}
                          aria-label="Column options"
                          onClick={(event: React.MouseEvent<HTMLElement>) =>
                            setEditColumnMenu({
                              open: true,
                              top: `${event.currentTarget.getBoundingClientRect().bottom}px`,
                              left: `${event.currentTarget.getBoundingClientRect().left - 130}px`,
                              right: 'auto',
                              bottom: 'auto',
                              columnNumber: columnNumber,
                              columnId: column.publicId,
                              rowNumber: -1,
                              rowId: ''
                            })
                          }
                        >
                          <Dots />
                        </Button>
                      )}
                    </div>

                    {column.description && !isEditorEmpty(column.description) && (
                      // As it was written (its lists, links and emphasis), in muted text under the label.
                      <div className={`-mt-1 ${READ_ONLY_DESCRIPTION}`}>
                        <Editor
                          key={`form-field-description-${column.publicId}-${descriptionKey}`}
                          databaseDoc={column.description}
                          readOnly={true}
                          editorId={`${spreadsheetData.viewDetails.publicId}-${column.publicId}`}
                          resources={[{ resource: 'document_table', publicId: spreadsheetData.tableDetails.publicId }]}
                        />
                      </div>
                    )}

                    {/* A checkbox and an attachment's files are their own controls, with no field around them. */}
                    {/* The field's input is named by the label above it: its name, and "Required Field". */}
                    <FormFieldLabel.Provider
                      value={
                        !column.isJoined && column.required
                          ? `form-field-name-${column.publicId} form-field-required-${column.publicId}`
                          : `form-field-name-${column.publicId}`
                      }
                    >
                      <div className={FRAMELESS_FORM_KINDS.includes(column.kind) ? 'form-field-cell' : FORM_FIELD}>
                        <Cell
                          key={`$new-row-form-column-${column.publicId}`}
                          value={formValues[column.publicId]}
                          rowNumber={-2000}
                          rowId="new-row-form"
                          prevRowId="new-row-form"
                          nextRowId="new-row-form"
                          columnNumber={columnNumber}
                          columnId={column.publicId}
                          nextColumnId={
                            columnNumber <= columns.length - 2 ? columns[columnNumber + 1].publicId : undefined
                          }
                          prevColumnId={columnNumber > 0 ? columns[columnNumber - 1].publicId : undefined}
                          setSelectedCell={setSelectedCell}
                          contextClick={setCellMenu}
                          kind={column.kind}
                          scriptEnabled={column.scriptEnabled}
                          formulaEnabled={column.formulaEnabled}
                          isJoined={column.isJoined}
                          formValues={formValues}
                          frozen={false}
                          rowHeight={spreadsheetData.viewDetails.rowHeight}
                          isContributor={spreadsheetData.isContributor}
                          isAdmin={spreadsheetData.isAdmin}
                          handleKeyDown={handleKeyDownForm}
                          uniqueNumber={uniqueNumber}
                          locked={isColumnLocked || column.isJoined || column.isSynced}
                          setCellValue={handleOnValueChange}
                          columnName={column.name}
                          selected={selected}
                          isExpanded={true}
                          editing={selected && selectedCell.editing}
                          tempValue={selected ? selectedCell.value : undefined}
                          setIsFocused={setIsFocused}
                          type={spreadsheetData.viewDetails.type}
                          colourSettings={spreadsheetData.userConfiguration.colourSettings}
                          columnValuesCount={spreadsheetData.columnValuesCount}
                          column={column}
                          startRowId={spreadsheetData.rows[0]?.publicId}
                          finalRowId={spreadsheetData.rows[spreadsheetData.rows.length - 1]?.publicId}
                          finalRowNumber={
                            spreadsheetData.visibleBitmap ? spreadsheetData.visibleCount : spreadsheetData.rows.length
                          }
                          startColumnId={spreadsheetData.viewDetails.columns[0]?.publicId}
                          finalColumnId={
                            spreadsheetData.viewDetails.columns[spreadsheetData.viewDetails.columns.length - 1].publicId
                          }
                          finalColumnNumber={spreadsheetData.viewDetails.columns.length}
                          allowComments={spreadsheetData.tableDetails.allowComments}
                          row={null}
                          className={FORM_FIELD_CELL}
                        />
                      </div>
                    </FormFieldLabel.Provider>
                    {/* The menus' grip, in the card's gutter beside the name, showing the field can be dragged. */}
                    {spreadsheetData.isAdmin && !isColumnLocked && (
                      <Drag
                        aria-hidden
                        className="absolute top-[5px] -left-[18px] size-3.5 cursor-grab text-faint opacity-0 transition-opacity group-hover/field:opacity-100"
                      />
                    )}
                    {/* Where the dragged field would land: the menus' blue line, in the gap beside or between fields. */}
                    {landing?.columnId === column.publicId && (
                      <div
                        aria-hidden
                        className={`pointer-events-none absolute rounded-full bg-blue ${dropLine(formWidth === 2, landing.side)}`}
                      />
                    )}
                  </div>
                )
              })}
            </div>

            {spreadsheetData.isContributor && (
              // A dialog's footer: its grey band to the card's corners, a message at the start and the buttons at
              // the end.
              <footer className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 rounded-b-lg border-t border-divider bg-surface-muted px-6 py-3.5">
                {!confirmSubmit && (
                  <>
                    {spreadsheetData.userConfiguration.unsavedChanges ? (
                      <span className="mr-auto text-red">
                        Please save changes to current view first, then submit form
                      </span>
                    ) : null}
                    <Button
                      internalType="outline"
                      className={BLUE_BUTTON}
                      disabled={spreadsheetData.userConfiguration.unsavedChanges}
                      onClick={() => setConfirmSubmit(true)}
                    >
                      Submit Form
                    </Button>
                  </>
                )}

                {confirmSubmit && (
                  <>
                    <p className="m-0 mr-auto">Are you sure you would like to submit the form?</p>
                    <Button
                      internalType="outline"
                      disabled={disable}
                      onClick={() => {
                        if (submitting) return
                        setConfirmSubmit(false)
                      }}
                    >
                      No
                    </Button>
                    <Button
                      internalType="outline"
                      className={BLUE_BUTTON}
                      disabled={disable}
                      onClick={handleSubmit}
                      isLoading={submitting}
                    >
                      Yes
                    </Button>
                  </>
                )}
              </footer>
            )}
          </>
        )}

        {formSubmitted && (
          <div className="flex flex-col items-start gap-4 px-6 pb-6">
            <p className="m-0">Thank you for your submission.</p>
            <Button
              internalType="outline"
              className={BLUE_BUTTON}
              data-testid="form-submit-button"
              onClick={handleResetSubmit}
            >
              Submit Another Form
            </Button>
          </div>
        )}
      </div>

      <ColumnMenu
        id={`column-context-menu-${uniqueNumber}`}
        menuState={editColumnMenu}
        setMenuState={setEditColumnMenu}
        width={264}
        setDeleteColumnModal={setDeleteColumnModal}
        setEditColumnMenu={(open: boolean, panel?: 'formula') => {
          setEditColumnMenu(INITIAL_CONTEXT_MENU_STATE)
          setChangeColumnMenu({
            open,
            top: editColumnMenu.top,
            left: editColumnMenu.left,
            right: editColumnMenu.right,
            bottom: editColumnMenu.bottom,
            columnNumber: editColumnMenu.columnNumber,
            columnId: editColumnMenu.columnId,
            columnPanel: panel
          })
        }}
      />

      {cellMenuColumn && (
        <FormCellMenu
          id={`form-cell-menu-${uniqueNumber}`}
          menuState={cellMenu}
          setMenuState={setCellMenu}
          width={200}
          column={cellMenuColumn}
          value={formValues[cellMenuColumn.publicId]}
          formValues={formValues}
          readOnly={isFieldReadOnly(cellMenuColumn)}
          onChange={(value) => void setFieldValue(cellMenuColumn.publicId, value)}
        />
      )}

      {changeColumnMenu.open && (
        <EditColumnMenu
          // One panel per column, as in the table: what the last column had still to save goes to that column.
          key={changeColumnMenu.columnId ?? 'none'}
          id={`change-column-context-menu-${uniqueNumber}`}
          menuState={changeColumnMenu}
          setMenuState={setChangeColumnMenu}
        />
      )}

      {deleteColumnModal && (
        <DeleteColumnModal
          id="delete-column-modal"
          open={deleteColumnModal}
          setOpen={setDeleteColumnModal}
          columnIds={editColumnMenu.columnId ? [editColumnMenu.columnId] : []}
        />
      )}
    </div>
  )
}

export default FormComponent
